/**
 * UML auto-generation utilities.
 * Pure functions for generating UML class diagram elements and connections
 * from entity, responsibility, and relationship data.
 */

export type RelationshipType =
 | 'association'
 | 'aggregation'
 | 'composition'
 | 'inheritance'
 | 'dependency';

export type Cardinality = '1-to-1' | '1-to-many' | 'many-to-one' | 'many-to-many';

export type AccessModifier = 'public' | 'private' | 'protected';

export interface Entity {
 id: string;
 name: string;
 description: string;
}

export interface Attribute {
 id: string;
 name: string;
 type: string;
 accessModifier: AccessModifier;
}

export interface Method {
 id: string;
 name: string;
 parameters: { name: string; type: string }[];
 returnType: string;
 accessModifier: AccessModifier;
}

export interface EntityResponsibility {
 entityId: string;
 attributes: Attribute[];
 methods: Method[];
}

export interface Relationship {
 id: string;
 fromEntityId: string;
 toEntityId: string;
 type: RelationshipType;
 cardinality: Cardinality;
}

export interface UMLField {
 access: '+' | '-' | '#';
 name: string;
 type: string;
}

export interface UMLMethod {
 access: '+' | '-' | '#';
 signature: string;
}

export interface UMLElement {
 id: string;
 type: 'class' | 'interface' | 'abstract-class';
 name: string;
 x: number;
 y: number;
 fields: UMLField[];
 methods: UMLMethod[];
}

export interface UMLConnection {
 id: string;
 fromId: string;
 toId: string;
 type: RelationshipType;
 cardinality: Cardinality;
}

export interface UMLDiagramData {
 elements: UMLElement[];
 connections: UMLConnection[];
}

/**
 * Maps an access modifier to its UML symbol.
 */
function toUMLAccess(modifier: AccessModifier): '+' | '-' | '#' {
 switch (modifier) {
  case 'public':
   return '+';
  case 'private':
   return '-';
  case 'protected':
   return '#';
 }
}

/**
 * Formats a method into a UML signature string.
 * Format: methodName(param1: type1, param2: type2): returnType
 */
function formatMethodSignature(method: Method): string {
 const params = method.parameters
  .map((p) => `${p.name}: ${p.type}`)
  .join(', ');
 return `${method.name}(${params}): ${method.returnType}`;
}

/**
 * Auto-generates a UML diagram from entities, their responsibilities,
 * and their relationships.
 *
 * - One UMLElement per entity with all its attributes/methods mapped
 * - UMLConnections from relationships with correct type and cardinality
 * - Elements are positioned in a grid layout for initial placement
 *
 * @param entities - Array of discovered entities
 * @param responsibilities - Map of entityId to responsibility (attributes/methods)
 * @param relationships - Array of relationships between entities
 * @returns UML diagram data with elements and connections
 */
export function autoGenerateUML(
 entities: Entity[],
 responsibilities: Record<string, EntityResponsibility>,
 relationships: Relationship[]
): UMLDiagramData {
 const elements: UMLElement[] = [];
 const connections: UMLConnection[] = [];

 // Grid layout constants
 const COLUMN_WIDTH = 300;
 const ROW_HEIGHT = 250;
 const COLUMNS = 3;
 const START_X = 50;
 const START_Y = 50;

 // Create one UMLElement per entity
 for (let i = 0; i < entities.length; i++) {
  const entity = entities[i];
  const responsibility = responsibilities[entity.id];

  // Compute grid position
  const col = i % COLUMNS;
  const row = Math.floor(i / COLUMNS);
  const x = START_X + col * COLUMN_WIDTH;
  const y = START_Y + row * ROW_HEIGHT;

  // Map attributes to UML fields
  const fields: UMLField[] = responsibility
   ? responsibility.attributes.map((attr) => ({
    access: toUMLAccess(attr.accessModifier),
    name: attr.name,
    type: attr.type,
   }))
   : [];

  // Map methods to UML methods
  const methods: UMLMethod[] = responsibility
   ? responsibility.methods.map((method) => ({
    access: toUMLAccess(method.accessModifier),
    signature: formatMethodSignature(method),
   }))
   : [];

  elements.push({
   id: `uml-${entity.id}`,
   type: 'class',
   name: entity.name,
   x,
   y,
   fields,
   methods,
  });
 }

 // Create UMLConnections from relationships
 for (const relationship of relationships) {
  connections.push({
   id: `conn-${relationship.id}`,
   fromId: `uml-${relationship.fromEntityId}`,
   toId: `uml-${relationship.toEntityId}`,
   type: relationship.type,
   cardinality: relationship.cardinality,
  });
 }

 return { elements, connections };
}
